import React, { useState, useRef, useEffect } from 'react';

/**
 * Custom dropdown with smooth open/close animation.
 * Options: array of { value, label } or string (used as value and label).
 */
function DropdownSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select...',
  label,
  id,
  name,
  required,
  className = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const normalizedOptions = options.map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt
  );
  const selected = normalizedOptions.find((o) => o.value === value);
  const displayValue = selected ? selected.label : '';

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (opt) => {
    onChange(opt.value);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={`dropdown ${isOpen ? 'dropdown--open' : ''} ${className}`}
    >
      {label && (
        <label className="form-label dropdown__label" htmlFor={id}>
          {label}
        </label>
      )}
      <button
        type="button"
        id={id}
        className={`dropdown__trigger ${value ? 'dropdown__trigger--has-value' : ''}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-required={required}
      >
        <span className="dropdown__value">
          {displayValue || placeholder}
        </span>
        <span className="dropdown__chevron" aria-hidden />
      </button>
      <div
        className="dropdown__panel"
        role="listbox"
        aria-hidden={!isOpen}
      >
        <div className="dropdown__list">
          {normalizedOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="option"
              aria-selected={opt.value === value}
              className={`dropdown__option ${opt.value === value ? 'dropdown__option--selected' : ''}`}
              onClick={() => handleSelect(opt)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      {name && (
        <input
          type="hidden"
          name={name}
          value={value}
          required={required}
          readOnly
          aria-hidden
        />
      )}
    </div>
  );
}

export default DropdownSelect;
